import { useCallback, useRef } from 'react';
import { Card, CardRequest, GetTransactionDetailsResponse } from 'shared/types/account/AuthorizeNetAccount';
import { TransactionRequest } from 'shared/types/payment';
import { sdk } from 'sdk';
import { UseAuthorizeNetReturn } from './types';
import { mutate } from 'swr';
import useSWR, { SWRResponse } from 'swr';
import { useAccount } from 'frontastic';

const useAuthorizeNet = (): UseAuthorizeNetReturn => {
  const { account } = useAccount();

  // We use account ID and default cc as part of the cache key to ensure user-specific data
  const result = useSWR(
    [
      '/action/authorize-net/getCards',
      account?.accountId,
      account?.defaultPaymentProfileId,
    ],
    () => sdk.composableCommerce.authorizeNet.getCards(),
    {
      keepPreviousData: true,
    },
  );
  const data = (result.data?.isError ? {} : { data: result.data?.data }) as SWRResponse<Card[]>;

  const deleteCard = useCallback(async (customerPaymentProfileId: string) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      customerPaymentProfileId,
    };
    const res = await extensions.authorizeNet.deleteCard(payload);
    if (!res.isError) {
      mutate(
        [
          '/action/authorize-net/getCards',
          account?.accountId,
          account?.defaultPaymentProfileId,
        ],
      );
    } else {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }
  }, []);

  const addCard = useCallback(async (card: CardRequest) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      ...card,
    };
    const res = await extensions.authorizeNet.addCard(payload);
    if (!res.isError) {
      mutate(
        [
          '/action/authorize-net/getCards',
          account?.accountId,
          account?.defaultPaymentProfileId,
        ],
      );
    } else {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return (res.isError ? {} : res.data) as Card;
  }, []);

  const updateCard = useCallback(async (card: CardRequest) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      ...card,
    };
    const res = await extensions.authorizeNet.updateCard(payload);
    if (!res.isError) {
      mutate(
        [
          '/action/authorize-net/getCards',
          account?.accountId,
          account?.defaultPaymentProfileId,
        ],
      );
    } else {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return (res.isError ? {} : res.data) as Card;
  }, []);

  const getCard = useCallback(async (card: CardRequest) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      ...card,
    };
    const res = await extensions.authorizeNet.getCard(payload);
    if (res.isError) {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return (res.isError ? {} : res.data) as Card;
  }, []);

  const getTransactionDetails = useCallback(async (transactionId: string) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      transactionId,
    };
    const res = await extensions.authorizeNet.getTransactionDetails(payload);
    if (res.isError) {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return res.isError ? ({} as GetTransactionDetailsResponse) : res.data;
  }, []);

  const makePayment = useCallback(async (request: TransactionRequest) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      ...request,
    };
    const res = await extensions.authorizeNet.makePayment(payload);
    if (res.isError) {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return res.isError ? {} : res.data;
  }, []);

  const makePaymentManualOrder = useCallback(async (request: TransactionRequest, orderId: string) => {
    const extensions = sdk.composableCommerce;
    const payload = {
      request,
      orderId,
    };
    const res = await extensions.authorizeNet.makePaymentManualOrder(payload);
    if (res.isError) {
      throw new Error((res.isError ? res.error.message : '') ?? 'Something went wrong');
    }

    return res.isError ? {} : res.data;
  }, []);

  return {
    ...data,
    deleteCard,
    addCard,
    updateCard,
    getCard,
    getTransactionDetails,
    makePayment,
    makePaymentManualOrder,
  };
};

export default useAuthorizeNet;
